// ---------------------------------------------------------------------------
// ConversationThread.tsx
//
// Renders the full list of ConversationTurns.
// BlockRenderer is the single dispatch point for all block types — both inline
// blocks (text, signals, chart) and ref-based embed blocks (event_embed,
// asset_embed, alert_embed, …). Each embed block component is responsible for
// fetching its own data.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import { useState } from 'react';
import {
    Box,
    Typography,
    Alert,
    Tooltip,
    Collapse,
    Divider,
    Chip,
    CircularProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EventIcon from '@mui/icons-material/Event';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

import type {
    ConversationTurn,
    ConversationBlock,
    TextBlock,
    AlertBannerBlock,
    SignalsBlock,
    ChartBlock,
    MarketEventsBlock,
    MarketEventItem,
    CollapsibleSectionBlock,
    EventEmbedBlock,
    AssetEmbedBlock,
    AlertEmbedBlock,
    SignalEmbedBlock,
    NewsEmbedBlock,
    MentionChipBlock,
    MentionRef,
    UserQuestionBlock,
} from '../api/aiSearchTypes';

import { AISignalCard } from './AISignalCard';
import { EmbeddedEventChart } from './charts/EmbeddedEventChart';
import { EmbeddedAssetChart } from './charts/EmbeddedAssetChart';
import { AIChartBlock } from './AIChartBlock';
import { openInNewTab } from '../../../utils/newTab';

// ---------------------------------------------------------------------------
// Markdown renderer
// ---------------------------------------------------------------------------

function useMarkdownComponents(): Components {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return {
        p: ({ children }) => (
            <Typography
                component="p"
                sx={{
                    fontSize: '13px',
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    mb: 0.75,
                }}
            >
                {children}
            </Typography>
        ),
        h1: ({ children }) => (
            <Typography
                component="h1"
                sx={{ fontSize: '15px', fontWeight: 700, mb: 0.5, mt: 1.5 }}
            >
                {children}
            </Typography>
        ),
        h2: ({ children }) => (
            <Typography
                component="h2"
                sx={{ fontSize: '14px', fontWeight: 700, mb: 0.5, mt: 1.5 }}
            >
                {children}
            </Typography>
        ),
        h3: ({ children }) => (
            <Typography
                component="h3"
                sx={{ fontSize: '13px', fontWeight: 600, mb: 0.5, mt: 1 }}
            >
                {children}
            </Typography>
        ),
        strong: ({ children }) => (
            <Typography
                component="strong"
                sx={{ fontWeight: 700, fontSize: 'inherit' }}
            >
                {children}
            </Typography>
        ),
        em: ({ children }) => (
            <Typography
                component="em"
                sx={{ fontStyle: 'italic', fontSize: 'inherit' }}
            >
                {children}
            </Typography>
        ),
        ul: ({ children }) => (
            <Box component="ul" sx={{ pl: 2.5, mb: 0.75, mt: 0 }}>
                {children}
            </Box>
        ),
        ol: ({ children }) => (
            <Box component="ol" sx={{ pl: 2.5, mb: 0.75, mt: 0 }}>
                {children}
            </Box>
        ),
        li: ({ children }) => (
            <Typography
                component="li"
                sx={{
                    fontSize: '13px',
                    color: 'text.secondary',
                    lineHeight: 1.7,
                }}
            >
                {children}
            </Typography>
        ),
        code: ({ children, className }) => {
            const isBlock = className?.includes('language-');
            return isBlock ? (
                <Box
                    component="pre"
                    sx={{
                        bgcolor: isDark
                            ? alpha('#fff', 0.06)
                            : alpha('#000', 0.04),
                        borderRadius: '6px',
                        p: 1.5,
                        overflowX: 'auto',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        my: 1,
                    }}
                >
                    <code>{children}</code>
                </Box>
            ) : (
                <Typography
                    component="code"
                    sx={{
                        fontSize: '11.5px',
                        fontFamily: 'monospace',
                        bgcolor: isDark
                            ? alpha('#fff', 0.08)
                            : alpha('#000', 0.06),
                        px: 0.5,
                        borderRadius: '3px',
                    }}
                >
                    {children}
                </Typography>
            );
        },
        blockquote: ({ children }) => (
            <Box
                component="blockquote"
                sx={{
                    borderLeft: 3,
                    borderColor: 'divider',
                    pl: 1.5,
                    my: 1,
                    color: 'text.disabled',
                }}
            >
                {children}
            </Box>
        ),
        hr: () => <Divider sx={{ my: 1.5 }} />,
        table: ({ children }) => (
            <Box
                component="table"
                sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    mb: 1,
                }}
            >
                {children}
            </Box>
        ),
        th: ({ children }) => (
            <Box
                component="th"
                sx={{
                    textAlign: 'left',
                    fontWeight: 600,
                    p: '4px 8px',
                    borderBottom: 2,
                    borderColor: 'divider',
                }}
            >
                {children}
            </Box>
        ),
        td: ({ children }) => (
            <Box
                component="td"
                sx={{
                    p: '4px 8px',
                    borderBottom: 1,
                    borderColor: 'divider',
                    verticalAlign: 'top',
                }}
            >
                {children}
            </Box>
        ),
        tr: ({ children }) => (
            <Box
                component="tr"
                sx={{
                    '&:hover': {
                        bgcolor: theme =>
                            theme.palette.mode === 'dark'
                                ? alpha('#fff', 0.04)
                                : alpha('#000', 0.03),
                    },
                }}
            >
                {children}
            </Box>
        ),
    };
}

// ---------------------------------------------------------------------------
// Inline block renderers
// ---------------------------------------------------------------------------

function TextBlockView({ block }: { block: TextBlock }) {
    const components = useMarkdownComponents();
    return (
        <Box
            sx={{
                '& > *:first-of-type': { mt: 0 },
                '& > *:last-of-type': { mb: 0 },
            }}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {block.content}
            </ReactMarkdown>
        </Box>
    );
}

function UserQuestionBlockView({ block }: { block: UserQuestionBlock }) {
    const components = useMarkdownComponents();
    return (
        <Box
            sx={{
                alignSelf: 'flex-end',
                maxWidth: '85%',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: t => alpha(t.palette.primary.main, 0.12),
                border: 1,
                borderColor: t => alpha(t.palette.primary.main, 0.2),
                borderRadius: '6px',
                px: 1.5,
                py: 1,
            }}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {block.content}
            </ReactMarkdown>
        </Box>
    );
}

const ALERT_ICONS = {
    info: InfoOutlinedIcon,
    warning: WarningAmberOutlinedIcon,
    error: ErrorOutlineIcon,
} as const;

function AlertBannerBlockView({ block }: { block: AlertBannerBlock }) {
    const Icon = ALERT_ICONS[block.severity];
    return (
        <Alert
            severity={block.severity}
            icon={<Icon fontSize="small" />}
            sx={{ fontSize: '12px', borderRadius: '6px' }}
        >
            <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.25 }}>
                {block.title}
            </Typography>
            <Typography
                sx={{ fontSize: '12px', color: 'inherit', opacity: 0.85 }}
            >
                {block.body}
            </Typography>
        </Alert>
    );
}

function SignalsBlockView({ block }: { block: SignalsBlock }) {
    if (block.signals.length === 0) return null;
    return (
        <Box>
            <Typography
                sx={{
                    fontSize: '10.5px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                    mb: 0.75,
                }}
            >
                Signals
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    columnGap: 2,
                }}
            >
                {block.signals.map((signal, i) => (
                    <AISignalCard key={i} signal={signal} />
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// MarketEvents block — list of event tiles (no chart; use event_embed for that)
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<MarketEventItem['status'], string> = {
    open: '#4caf50',
    closed: '#9e9e9e',
    resolved: '#2196f3',
};

function MarketEventTile({
    event,
    selected,
    onSelect,
}: {
    event: MarketEventItem;
    selected: boolean;
    onSelect: () => void;
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const pct =
        event.probability !== undefined
            ? Math.round(event.probability * 100)
            : null;

    const href = event.slug
        ? `/markets/event/${event.slug}`
        : `/markets/event/${event.event_id}`;

    return (
        <Box
            onClick={onSelect}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1.5,
                border: 1,
                borderColor: selected ? 'primary.main' : 'divider',
                borderRadius: '6px',
                cursor: 'pointer',
                bgcolor: selected
                    ? alpha(theme.palette.primary.main, isDark ? 0.1 : 0.05)
                    : 'transparent',
                '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: alpha(
                        theme.palette.primary.main,
                        isDark ? 0.08 : 0.04
                    ),
                },
                transition: 'all 0.15s',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            lineHeight: 1.35,
                        }}
                    >
                        {event.title}
                    </Typography>
                    {event.relevance_note && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'text.disabled',
                                mt: 0.25,
                            }}
                        >
                            {event.relevance_note}
                        </Typography>
                    )}
                </Box>
                {pct !== null && (
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 700,
                            color: pct > 50 ? 'success.main' : 'text.secondary',
                            flexShrink: 0,
                        }}
                    >
                        {pct}%
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: STATUS_COLOR[event.status],
                        flexShrink: 0,
                    }}
                />
                <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
                    {event.status}
                </Typography>
                {event.volume && (
                    <>
                        <Typography
                            sx={{ fontSize: '10px', color: 'text.disabled' }}
                        >
                            ·
                        </Typography>
                        <Typography
                            sx={{ fontSize: '10.5px', color: 'text.disabled' }}
                        >
                            {event.volume}
                        </Typography>
                    </>
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Open event">
                    <OpenInNewIcon
                        onClick={e => {
                            e.stopPropagation();
                            openInNewTab(href);
                        }}
                        sx={{
                            fontSize: 13,
                            color: 'text.disabled',
                            cursor: 'pointer',
                        }}
                    />
                </Tooltip>
            </Box>
        </Box>
    );
}

function MarketEventsBlockView({ block }: { block: MarketEventsBlock }) {
    const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
    const selected = selectedIdx !== null ? block.events[selectedIdx] : null;

    return (
        <Box>
            {block.label && (
                <Typography
                    sx={{
                        fontSize: '10.5px',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                        mb: 0.75,
                    }}
                >
                    {block.label}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1,
                }}
            >
                {block.events.map((event, i) => (
                    <MarketEventTile
                        key={event.event_id}
                        event={event}
                        selected={selectedIdx === i}
                        onSelect={() =>
                            setSelectedIdx(i === selectedIdx ? null : i)
                        }
                    />
                ))}
            </Box>

            {/* Inline chart for selected event — spans full width below the grid */}
            <Collapse in={selected !== null}>
                {selected && (
                    <Box sx={{ mt: 1.5 }}>
                        <EmbeddedEventChart
                            eventId={selected.event_id}
                            slug={selected.slug}
                            timeRange={selected.time_range}
                            conditionIdFilter={selected.highlight_condition_ids}
                            signalTypeFilter={selected.signal_type_filter}
                            showAlerts={selected.show_alerts}
                            height={360}
                        />
                    </Box>
                )}
            </Collapse>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// CollapsibleSection block
// ---------------------------------------------------------------------------

function CollapsibleSectionBlockView({
    block,
}: {
    block: CollapsibleSectionBlock;
}) {
    const [open, setOpen] = useState(block.default_open ?? false);

    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '6px',
                overflow: 'hidden',
            }}
        >
            <Box
                onClick={() => setOpen(prev => !prev)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.875,
                    cursor: 'pointer',
                    bgcolor: t =>
                        t.palette.mode === 'dark'
                            ? alpha('#fff', 0.04)
                            : alpha('#000', 0.03),
                    '&:hover': {
                        bgcolor: t =>
                            t.palette.mode === 'dark'
                                ? alpha('#fff', 0.07)
                                : alpha('#000', 0.05),
                    },
                    userSelect: 'none',
                    transition: 'background-color 0.15s',
                }}
            >
                <ExpandMoreIcon
                    sx={{
                        fontSize: 16,
                        color: 'text.disabled',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s',
                        flexShrink: 0,
                    }}
                />
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'text.secondary',
                    }}
                >
                    {block.title}
                </Typography>
            </Box>
            <Collapse in={open}>
                <Box
                    sx={{
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        borderTop: 1,
                        borderColor: 'divider',
                    }}
                >
                    {block.blocks.map((child, i) => (
                        <BlockRenderer key={i} block={child} />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Ref-based embed block renderers
// Each is a stub that will be fleshed out as the real components are built.
// They accept the typed block and are responsible for their own data fetching.
// ---------------------------------------------------------------------------

function EventEmbedBlockView({ block }: { block: EventEmbedBlock }) {
    return (
        <Box>
            {block.context && (
                <Typography
                    sx={{
                        fontSize: '11.5px',
                        color: 'text.secondary',
                        mb: 1,
                        fontStyle: 'italic',
                    }}
                >
                    {block.context}
                </Typography>
            )}
            <EmbeddedEventChart
                eventId={block.event_id}
                slug={block.slug}
                timeRange={block.time_range}
                conditionIdFilter={block.highlight_condition_ids}
                signalTypeFilter={block.signal_type_filter}
                showAlerts={block.show_alerts}
                height={360}
            />
        </Box>
    );
}

function AssetEmbedBlockView({ block }: { block: AssetEmbedBlock }) {
    return (
        <Box>
            {block.context && (
                <Typography
                    sx={{
                        fontSize: '11.5px',
                        color: 'text.secondary',
                        mb: 1,
                        fontStyle: 'italic',
                    }}
                >
                    {block.context}
                </Typography>
            )}
            <EmbeddedAssetChart
                ticker={block.ticker}
                assetType={block.asset_type}
                timeRange={block.time_range}
                height={360}
            />
        </Box>
    );
}

function AlertEmbedBlockView({ block }: { block: AlertEmbedBlock }) {
    // TODO: replace with AlertContextCard once built (fetches /api/alerts/{id})
    return (
        <Box
            sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: '6px',
                bgcolor: t => alpha(t.palette.warning.main, 0.06),
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
            >
                <NotificationsIcon
                    sx={{ fontSize: 14, color: 'warning.main' }}
                />
                <Typography
                    sx={{
                        fontSize: '10.5px',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                    }}
                >
                    Alert
                </Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                {block.label ?? block.alert_id}
            </Typography>
            {block.event_ref && (
                <Box sx={{ mt: 1.5 }}>
                    <EmbeddedEventChart
                        eventId={block.event_ref.event_id}
                        slug={block.event_ref.slug}
                        timeRange={block.event_ref.time_range}
                        showAlerts={block.event_ref.show_alerts}
                        height={280}
                    />
                </Box>
            )}
        </Box>
    );
}

function SignalEmbedBlockView({ block }: { block: SignalEmbedBlock }) {
    // TODO: replace with SignalContextCard once built
    return (
        <Box
            sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: '6px',
            }}
        >
            <Typography
                sx={{
                    fontSize: '10.5px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                    mb: 0.5,
                }}
            >
                Signal
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                {block.label ?? block.signal_id}
            </Typography>
        </Box>
    );
}

function NewsEmbedBlockView({ block }: { block: NewsEmbedBlock }) {
    // TODO: replace with NewsContextCard once built
    return (
        <Box
            sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: '6px',
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
            >
                <ArticleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography
                    sx={{
                        fontSize: '10.5px',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                    }}
                >
                    {block.source ?? 'News'}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                {block.title ?? block.article_id}
            </Typography>
            {block.published_at && (
                <Typography
                    sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.25 }}
                >
                    {block.published_at.slice(0, 10)}
                </Typography>
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Mention chip — user turn only
// ---------------------------------------------------------------------------

const MENTION_ICONS: Record<MentionRef['type'], typeof EventIcon> = {
    event: EventIcon,
    asset: ShowChartIcon,
    alert: NotificationsIcon,
    signal: ShowChartIcon,
    news: ArticleIcon,
};

const MENTION_SIGIL: Record<MentionRef['type'], string> = {
    event: '@',
    asset: '$',
    alert: '#',
    signal: '#',
    news: '~',
};

function MentionChipBlockView({ block }: { block: MentionChipBlock }) {
    const Icon = MENTION_ICONS[block.ref.type];
    return (
        <Chip
            size="small"
            icon={<Icon sx={{ fontSize: '12px !important' }} />}
            label={`${MENTION_SIGIL[block.ref.type]}${block.ref.label}`}
            sx={{
                fontSize: '11px',
                height: 22,
                borderRadius: '4px',
                fontWeight: 500,
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// BlockRenderer — single dispatch point for all block types
// ---------------------------------------------------------------------------

export function BlockRenderer({ block }: { block: ConversationBlock }) {
    switch (block.type) {
        // inline
        case 'user_question':
            return <UserQuestionBlockView block={block} />;
        case 'text':
            return <TextBlockView block={block} />;
        case 'alert_banner':
            return <AlertBannerBlockView block={block} />;
        case 'signals':
            return <SignalsBlockView block={block} />;
        case 'chart':
            return <AIChartBlock block={block} />;
        case 'market_events':
            return <MarketEventsBlockView block={block} />;
        case 'collapsible_section':
            return <CollapsibleSectionBlockView block={block} />;
        // ref-based embeds
        case 'event_embed':
            return <EventEmbedBlockView block={block} />;
        case 'asset_embed':
            return <AssetEmbedBlockView block={block} />;
        case 'alert_embed':
            return <AlertEmbedBlockView block={block} />;
        case 'signal_embed':
            return <SignalEmbedBlockView block={block} />;
        case 'news_embed':
            return <NewsEmbedBlockView block={block} />;
        // user-only
        case 'mention_chip':
            return <MentionChipBlockView block={block} />;
    }
}

// ---------------------------------------------------------------------------
// TurnView — renders one conversation turn
// ---------------------------------------------------------------------------

function TurnView({ turn }: { turn: ConversationTurn }) {
    const isUser = turn.role === 'user';
    const isSystem = turn.role === 'system';

    if (isUser) {
        // User turn: right-aligned bubble; text and mention chips flow inline
        return (
            <Box
                sx={{
                    alignSelf: 'flex-end',
                    maxWidth: '85%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.75,
                    bgcolor: t => alpha(t.palette.primary.main, 0.12),
                    border: 1,
                    borderColor: t => alpha(t.palette.primary.main, 0.2),
                    borderRadius: '6px',
                    px: 1.5,
                    py: 1,
                }}
            >
                {turn.blocks.map((block, i) => (
                    <BlockRenderer key={i} block={block} />
                ))}
            </Box>
        );
    }

    // System and assistant turns: left-aligned, full width, vertical stack
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {isSystem && (
                <Typography
                    sx={{
                        fontSize: '10px',
                        color: 'text.disabled',
                        letterSpacing: '0.06em',
                    }}
                >
                    CONTEXT
                </Typography>
            )}
            {turn.blocks.map((block, i) => (
                <BlockRenderer key={i} block={block} />
            ))}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// ConversationThread — public export
// ---------------------------------------------------------------------------

interface ConversationThreadProps {
    turns: ConversationTurn[];
}

export function ConversationThread({ turns }: ConversationThreadProps) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {turns.map((turn, idx) => (
                <TurnView key={idx} turn={turn} />
            ))}
        </Box>
    );
}
